import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'
import {
  domainPanelQuery,
  refreshAdInventory,
  startGpoDeploy,
  type DomainAgentComputer,
  type DomainMissingComputer,
  type DomainCoverageGroup,
  type DomainOu,
  type DomainPanel,
  type GpoKind,
  type GpoMachineStage,
  type GpoRunState,
} from '../api/devices'
import { toast } from '../ui/notify'
import './DeviceDomainDeploy.css'

/**
 * Deploy-ul agentului prin Group Policy, pe fila Functions a unei masini care
 * a raportat ca e controler de domeniu.
 *
 * Trei lucruri pe acelasi card: CAT din domeniu are deja agentul (acoperirea,
 * din inventarul de AD adus de comanda ADI), UNDE pleaca politica (tot
 * domeniul sau OU-urile bifate) si CUM a iesit ultima rulare (comanda GPO).
 * Agentul de pe controlor face politica singur, cu cheia firmei.
 */
export function DeviceDomainDeploy({ deviceId }: { deviceId: number }) {
  const { data, error } = useQuery(domainPanelQuery(deviceId))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert" id="agentGpoDeploy">
        The domain panel could not be loaded: {error.message}
      </div>
    )
  }
  if (!data) return null
  return <Panel deviceId={deviceId} panel={data} />
}

const STATE_LABEL: Record<GpoRunState, string> = {
  waiting: 'Waiting for the agent',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
}

const STATE_ICON: Record<GpoRunState, string> = {
  waiting: 'far fa-clock',
  running: 'fas fa-circle-notch fa-spin',
  succeeded: 'fas fa-check',
  failed: 'fas fa-times',
}

function Panel({ deviceId, panel }: { deviceId: number; panel: DomainPanel }) {
  const query = useQueryClient()
  const ous = panel.domain?.ous ?? []
  const [mode, setMode] = useState<'domain' | 'ous'>(() => (panel.lastRun && panel.lastRun.ous.length > 0 && ous.length > 0 ? 'ous' : 'domain'))
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set((panel.lastRun?.ous ?? []).filter((dn) => ous.some((o) => o.dn.toLowerCase() === dn.toLowerCase()))),
  )
  const [kind, setKind] = useState<GpoKind>(() => panel.lastRun?.kind ?? 'both')
  const [busy, setBusy] = useState<'' | 'read' | 'deploy'>('')
  const [showOutput, setShowOutput] = useState(false)
  const [note, setNote] = useState('')

  /* Ce e bifat si nu mai e in inventar (OU stearsa intre timp) nu pleaca. */
  const known = useMemo(() => new Set(ous.map((o) => o.dn.toLowerCase())), [ous])
  const selected = [...chosen].filter((dn) => known.has(dn.toLowerCase()))

  const reload = () => query.invalidateQueries({ queryKey: ['device', deviceId, 'agent-domain'] })

  const read = async () => {
    setBusy('read')
    try {
      await refreshAdInventory(deviceId)
      await reload()
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Active Directory could not be read.', 'Group Policy', 'error')
    } finally {
      setBusy('')
    }
  }

  const deploy = async () => {
    const what = KIND_NOUN[kind]
    const where =
      mode === 'domain'
        ? `every Windows ${what.one} in ${panel.domain?.name || 'the domain'}`
        : `the ${what.many} in ${selected.length === 1 ? '1 organizational unit' : `${selected.length} organizational units`} and the units below them`
    if (!window.confirm(`This creates a Group Policy that installs the OptimumDesk agent on ${where}. Continue?`)) return
    setBusy('deploy')
    try {
      const out = await startGpoDeploy(deviceId, mode === 'ous' ? selected : [], kind)
      setNote(
        out.pending
          ? 'Already started - the agent on this domain controller has not picked it up yet.'
          : 'Started. The agent on this domain controller creates the policy in the next minutes; machines install at their next policy refresh.',
      )
      await reload()
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not start the deployment.', 'Group Policy', 'error')
    } finally {
      setBusy('')
    }
  }

  const noOus = mode === 'ous' && selected.length === 0
  const goLabel =
    busy === 'deploy'
      ? 'Starting...'
      : mode === 'domain'
        ? 'Deploy to the whole domain'
        : selected.length === 1
          ? 'Deploy to 1 organizational unit'
          : `Deploy to ${selected.length} organizational units`

  return (
    <section className="af-card gp" id="agentGpoDeploy">
      <header className="gp-head">
        <div className="af-icon">
          <i className="fab fa-windows"></i>
        </div>
        <div className="gp-title">
          <h4>Deploy the agent through Group Policy</h4>
          <div className="gp-sub" id="agentGpoDomain">
            {panel.domain ? (
              <>
                Domain <strong>{panel.domain.name || 'unnamed'}</strong>
                <span className="gp-dot">&middot;</span>
                read from Active Directory {panel.domain.collectedAt}
              </>
            ) : (
              'This machine is a domain controller. Its Active Directory has not been read yet.'
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-default btn-sm gp-refresh"
          id="agentAdRefresh"
          disabled={busy !== '' || panel.readingPending}
          onClick={() => void read()}
        >
          <i className={panel.readingPending || busy === 'read' ? 'fas fa-circle-notch fa-spin' : 'fas fa-sync-alt'}></i>
          {panel.readingPending ? 'Reading Active Directory...' : 'Refresh from Active Directory'}
        </button>
      </header>

      <div className="gp-body">
        <div className="gp-col" id="agentGpoCoverage">
          <div className="gp-k">Coverage</div>
          <Coverage panel={panel} units={mode === 'ous' && selected.length > 0 ? selected : null} kind={kind} />
        </div>
        <div className="gp-col">
          <div className="gp-k">Where to deploy</div>
          <div className="gp-modes" role="radiogroup">
            <label className={`gp-mode${mode === 'domain' ? ' on' : ''}`}>
              <input type="radio" name="gpoMode" value="domain" checked={mode === 'domain'} onChange={() => setMode('domain')} />
              <span>
                <strong>Whole domain</strong>
                <small>Every Windows computer in {panel.domain?.name || 'the domain'}</small>
              </span>
            </label>
            <label className={`gp-mode${mode === 'ous' ? ' on' : ''}${ous.length === 0 ? ' off' : ''}`}>
              <input
                type="radio"
                name="gpoMode"
                value="ous"
                checked={mode === 'ous'}
                disabled={ous.length === 0}
                onChange={() => setMode('ous')}
              />
              <span>
                <strong>Chosen organizational units</strong>
                <small>
                  {ous.length === 0
                    ? panel.domain
                      ? 'The domain reported no organizational units'
                      : 'Read Active Directory first'
                    : 'Only the units you tick, and the units below them'}
                </small>
              </span>
            </label>
          </div>
          <div className="gp-kinds" role="radiogroup" aria-label="Which computers" id="agentGpoKind">
            <span className="gp-kinds-k">Install on</span>
            {(['both', 'servers', 'workstations'] as const).map((k) => (
              <label key={k} className={`gp-kind${kind === k ? ' on' : ''}`}>
                <input type="radio" name="gpoKind" value={k} checked={kind === k} onChange={() => setKind(k)} />
                {KIND_LABEL[k]}
              </label>
            ))}
          </div>
          {kind !== 'both' ? (
            <p className="gp-hint" id="agentGpoKindHint">
              The policy still reaches every computer in the chosen place; the {KIND_NOUN[kind === 'servers' ? 'workstations' : 'servers'].many} skip
              the installation and show as excluded by the filter.
            </p>
          ) : null}
          {mode === 'ous' && ous.length > 0 ? (
            <OuPicker ous={ous} chosen={chosen} setChosen={setChosen} missing={panel.coverage} />
          ) : null}
        </div>
      </div>

      {note !== '' ? (
        <div className="gp-note" id="agentGpoDone">
          {note}
        </div>
      ) : null}

      <footer className="gp-foot">
        <div className="gp-last" id="agentGpoLast">
          <span className="gp-k">Last deployment</span>
          {panel.lastRun ? (
            <>
              <span className={`gp-pill ${panel.lastRun.state}`}>
                <i className={STATE_ICON[panel.lastRun.state]}></i> {STATE_LABEL[panel.lastRun.state]}
              </span>
              <span>{panel.lastRun.answeredAt || panel.lastRun.createdAt}</span>
              <span className="gp-dot">&middot;</span>
              <span title={panel.lastRun.ous.join('\n')}>
                {panel.lastRun.ous.length === 0
                  ? 'Whole domain'
                  : panel.lastRun.ous.length === 1
                    ? '1 organizational unit'
                    : `${panel.lastRun.ous.length} organizational units`}
                {panel.lastRun.kind && panel.lastRun.kind !== 'both' ? `, ${KIND_NOUN[panel.lastRun.kind].many} only` : ''}
              </span>
              {panel.lastRun.message.trim() !== '' ? (
                <button type="button" className="gp-link" id="agentGpoOutputToggle" onClick={() => setShowOutput(!showOutput)}>
                  {showOutput ? 'Hide output' : 'Show output'}
                  <i className={showOutput ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i>
                </button>
              ) : null}
            </>
          ) : (
            <span>Never run from here.</span>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary gp-go"
          id="agentGpoStart"
          disabled={busy !== '' || panel.deployPending || noOus}
          title={noOus ? 'Tick at least one organizational unit.' : undefined}
          onClick={() => void deploy()}
        >
          {panel.deployPending ? 'Waiting for the agent...' : goLabel}
        </button>
      </footer>
      {showOutput && panel.lastRun ? (
        <pre className="gp-out" id="agentGpoOutput">
          {panel.lastRun.message}
        </pre>
      ) : null}
    </section>
  )
}

const MACHINE_LABEL: Record<GpoMachineStage, string> = {
  failed: 'Failed',
  started: 'Installing',
  installed: 'Installed, not reporting yet',
  present: 'Already installed',
  excluded: 'Excluded by the filter',
}

const KIND_LABEL: Record<GpoKind, string> = {
  both: 'Both',
  servers: 'Servers',
  workstations: 'Workstations',
}

const KIND_NOUN: Record<GpoKind, { one: string; many: string }> = {
  both: { one: 'computer', many: 'computers' },
  servers: { one: 'server', many: 'servers' },
  workstations: { one: 'workstation', many: 'workstations' },
}

type CoverageCounts = Omit<DomainCoverageGroup, 'parent' | 'server'>

/** Calculatorul din containerul `parent` sta sub una din OU-urile alese (sau chiar in ea). */
function inUnits(parent: string, units: string[]): boolean {
  const p = parent.toLowerCase()
  return units.some((u) => p === u.toLowerCase() || isBelow(parent, u))
}

/** Acelasi test ca DomainCoverage::isServer din API: serverele isi spun numele in sistemul de operare. */
function isServerOs(os: string): boolean {
  return /server/i.test(os)
}

function ofKind(server: boolean, kind: GpoKind): boolean {
  return kind === 'both' || (kind === 'servers') === server
}

/**
 * Acoperirea numai pe calculatoarele alese: bucatile (`groups`) al caror
 * container e sub una din OU-uri (`null` = tot domeniul) si care sunt de
 * felul ales (servere / statii). Acelasi test ca la bifarea OU-urilor.
 */
export function scopedCoverage(groups: DomainCoverageGroup[], units: string[] | null, kind: GpoKind = 'both'): CoverageCounts {
  const out: CoverageCounts = { total: 0, covered: 0, failed: 0, started: 0, installed: 0, present: 0, excluded: 0, notReached: 0 }
  for (const g of groups) {
    if (units !== null && !inUnits(g.parent, units)) continue
    if (!ofKind(g.server, kind)) continue
    out.total += g.total
    out.covered += g.covered
    out.failed += g.failed
    out.started += g.started
    out.installed += g.installed
    out.present += g.present
    out.excluded += g.excluded ?? 0
    out.notReached += g.notReached
  }
  return out
}

function Coverage({ panel, units, kind }: { panel: DomainPanel; units: string[] | null; kind: GpoKind }) {
  const all = panel.coverage
  const [open, setOpen] = useState(() => (all?.gpo.failed ?? 0) > 0)
  const [openOutside, setOpenOutside] = useState(false)
  if (!all) {
    return (
      <div className="gp-empty">
        <i className={panel.readingPending ? 'fas fa-circle-notch fa-spin' : 'fas fa-desktop'}></i>
        {panel.readingPending
          ? 'Reading Active Directory. The agent on this domain controller answers within a few minutes.'
          : 'Refresh from Active Directory to see how many computers in the domain already have the agent, and which do not.'}
      </div>
    )
  }
  /* Pe OU-uri alese sau pe un singur fel de masini, cifra mare e despre ele;
     restul domeniului se spune separat, pe un rand mic. */
  const scoped = (units !== null || kind !== 'both') && Array.isArray(all.groups)
  const cov: CoverageCounts = scoped
    ? scopedCoverage(all.groups, units, kind)
    : { total: all.total, covered: all.covered, ...all.gpo, excluded: all.gpo.excluded ?? 0 }
  const outside = scoped ? all.total - cov.total : 0
  const complete = all.missing.length === all.missingTotal
  const inScope = (pc: { dn: string; os: string }) =>
    (units === null || (pc.dn !== '' && inUnits(parentDn(pc.dn), units))) && ofKind(isServerOs(pc.os), kind)
  const rows = scoped && complete ? all.missing.filter(inScope) : all.missing
  const rowsTotal = scoped && complete ? rows.length : all.missingTotal
  /* Cele din afara selectiei: fara agent intai (pe ele e ceva de facut), apoi
     cele care il au deja - printre ele, de obicei, chiar controlerul. */
  const withAgent: DomainAgentComputer[] = all.withAgent ?? []
  const outsideAgent = scoped ? all.covered - cov.covered : 0
  const outsideRows: TableRow[] =
    outside > 0 && Array.isArray(all.withAgent)
      ? [
          ...all.missing.filter((pc) => !inScope(pc)),
          ...withAgent.filter((pc) => !inScope(pc)).map((pc): TableRow => ({ ...pc, gpo: null, hasAgent: true })),
        ]
      : []
  const source = panel.source ?? ''
  /* Cele lasate deoparte de filtru nu sunt "fara agent": se numara separat. */
  const missing = cov.total - cov.covered - cov.excluded
  const pct = cov.total === 0 ? 0 : Math.round((cov.covered / cov.total) * 100)
  const noun = KIND_NOUN[scoped ? kind : 'both']
  const where = scoped && units !== null ? ' in the selected units' : ''
  const selection = units !== null && kind === 'both' ? 'the selected units' : 'the selection'
  return (
    <>
      <div className="gp-big">
        <span className="gp-num" id="agentGpoCovered">
          {cov.covered}
        </span>
        <span className="gp-of">
          of {cov.total} {cov.total === 1 ? noun.one : noun.many}
          {where} {cov.total === 1 ? 'has' : 'have'} the agent
        </span>
      </div>
      <div className="gp-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${pct}%` }}></span>
      </div>
      {outside > 0 ? (
        <div className="gp-outside" id="agentGpoOutside">
          {outside} more {outside === 1 ? 'computer is' : 'computers are'} outside {selection}
          {outsideAgent > 0 ? `, ${outsideAgent} of them ${outsideAgent === 1 ? 'has' : 'have'} the agent` : ''}
          {outsideRows.length > 0 ? (
            <>
              <button type="button" className="gp-link" id="agentGpoOutsideToggle" onClick={() => setOpenOutside(!openOutside)}>
                {openOutside ? `Hide the computers outside ${selection}` : `Show the computers outside ${selection}`}
                <i className={openOutside ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i>
              </button>
              {openOutside ? <MissingTable id="agentGpoOutsideList" rows={outsideRows} total={outside} source={source} kind={kind} /> : null}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="gp-meta">
        {missing > 0 ? <span className="gp-warn">{missing} without the agent</span> : null}
        {missing > 0 && all.disabled > 0 ? <span className="gp-dot">&middot;</span> : null}
        {all.disabled > 0
          ? `${all.disabled} disabled computer ${all.disabled === 1 ? 'account is' : 'accounts are'} not counted`
          : null}
      </div>
      {missing + cov.excluded > 0 && (panel.lastRun !== null || cov.failed + cov.started + cov.installed + cov.present + cov.excluded > 0) ? (
        <div className="gp-states" id="agentGpoStates">
          {cov.failed > 0 ? <span className="gp-state failed">{cov.failed} failed</span> : null}
          {cov.started > 0 ? <span className="gp-state started">{cov.started} installing</span> : null}
          {cov.installed > 0 ? (
            <span className="gp-state installed" title="The installation finished, but the agent has not appeared in the inventory yet.">
              {cov.installed} installed, not reporting yet
            </span>
          ) : null}
          {cov.present > 0 ? <span className="gp-state present">{cov.present} already installed</span> : null}
          {cov.notReached > 0 ? (
            <span className="gp-state none" title="These computers have not reported anything: the policy has not reached them yet. They pick it up at their next restart.">
              {cov.notReached} not reached yet
            </span>
          ) : null}
          {cov.excluded > 0 ? (
            <span className="gp-state excluded" title="The policy reached these computers, but they are not of the kind chosen in Where to deploy, so nothing was installed.">
              {cov.excluded} excluded by the filter
            </span>
          ) : null}
        </div>
      ) : null}
      {missing === 0 ? (
        <div className="gp-all">
          <i className="fas fa-check-circle"></i> Every enabled {noun.one} {where !== '' ? 'in the selected units' : 'in the domain'} has the agent.
        </div>
      ) : (
        <>
          <button type="button" className="gp-link" id="agentGpoMissingToggle" onClick={() => setOpen(!open)}>
            {open ? 'Hide the computers without the agent' : 'Show the computers without the agent'}
            <i className={open ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i>
          </button>
          {open ? <MissingTable id="agentGpoMissing" rows={rows} total={rowsTotal} source={source} kind={kind} /> : null}
        </>
      )}
    </>
  )
}

/** Un cont neatins de 90 de zile e probabil o masina moarta: se vede mai stins. */
function isOld(lastLogon: string): boolean {
  if (lastLogon === '') return true
  const at = Date.parse(lastLogon.replace(' ', 'T'))
  return Number.isFinite(at) && Date.now() - at > 90 * 86400000
}

/** Un rand din tabel: fara agent (cu starea GPO), sau cu agent (lista din afara selectiei). */
type TableRow = DomainMissingComputer & { hasAgent?: boolean }

/** Numele scurt, fara majuscule - acelasi test ca DomainCoverage::short din API. */
function shortName(name: string): string {
  return name.trim().toLowerCase().split('.')[0]
}

function MissingTable({ id, rows, total, source, kind }: { id: string; rows: TableRow[]; total: number; source: string; kind: GpoKind }) {
  const [shown, setShown] = useState<string>(() => rows.find((pc) => pc.gpo?.stage === 'failed')?.name ?? '')
  /* Listele urmeaza filtrul de deploy "Install on": pe Both toate, altfel numai felul ales. */
  const src = shortName(source)
  const visible = kind === 'both' ? rows : rows.filter((pc) => ofKind(isServerOs(pc.os), kind))
  return (
    <>
      {kind !== 'both' ? (
        <p className="gp-hint gp-follow" id={`${id}Note`}>
          Showing {KIND_NOUN[kind].many} only, as chosen under Install on.
        </p>
      ) : null}
      <div className="gp-miss" id={id}>
        <table>
          <thead>
            <tr>
              <th className="gp-c1">Computer</th>
              <th className="gp-c2">Group Policy</th>
              <th className="gp-c3">Last logon</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr className="gp-nokind">
                <td colSpan={3}>No computers of this kind.</td>
              </tr>
            ) : null}
            {visible.map((pc) => {
              const failed = pc.gpo?.stage === 'failed' && pc.gpo.message.trim() !== ''
              const isShown = failed && shown === pc.name
              return (
                <Fragment key={pc.dn || pc.name}>
                  <tr
                    className={failed ? 'gp-can' : undefined}
                    data-name={pc.name}
                    onClick={failed ? () => setShown(isShown ? '' : pc.name) : undefined}
                  >
                    <td title={pc.dn}>
                      {pc.name}
                      {src !== '' && shortName(pc.name) === src ? (
                        <span className="gp-tag src" title="The domain controller this deployment runs from">
                          Deployment source
                        </span>
                      ) : null}
                      <small>{pc.os || '-'}</small>
                    </td>
                    <td>
                      {pc.hasAgent ? (
                        <span className="gp-state agent">Has the agent</span>
                      ) : pc.gpo ? (
                        <span className={`gp-state ${pc.gpo.stage}`} title={`${MACHINE_LABEL[pc.gpo.stage]} - ${pc.gpo.reportedAt}`}>
                          {MACHINE_LABEL[pc.gpo.stage]}
                          {failed ? <i className={isShown ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i> : null}
                        </span>
                      ) : (
                        <span className="gp-none">Not reached yet</span>
                      )}
                    </td>
                    <td className={isOld(pc.lastLogon) ? 'gp-old' : undefined} title={pc.lastLogon}>
                      {pc.lastLogon.slice(0, 10) || 'never'}
                    </td>
                  </tr>
                  {isShown && pc.gpo ? (
                    <tr className="gp-why">
                      <td colSpan={3}>
                        <span>{pc.gpo.reportedAt}</span> {pc.gpo.message}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        {total > rows.length ? <div className="gp-more">and {total - rows.length} more</div> : null}
      </div>
    </>
  )
}

interface OuNode {
  dn: string
  name: string
  children: OuNode[]
}

/** Parintele unui DN: tot ce e dupa prima virgula. */
function parentDn(dn: string): string {
  const i = dn.indexOf(',')
  return i < 0 ? '' : dn.slice(i + 1)
}

/** Arborele de OU-uri, din DN-uri. O OU al carei parinte nu e OU sta la radacina. */
export function buildOuTree(ous: DomainOu[]): OuNode[] {
  const nodes = new Map<string, OuNode>()
  for (const ou of ous) nodes.set(ou.dn.toLowerCase(), { dn: ou.dn, name: ou.name || ou.dn, children: [] })
  const roots: OuNode[] = []
  for (const node of nodes.values()) {
    const parent = nodes.get(parentDn(node.dn).toLowerCase())
    if (parent) parent.children.push(node)
    else roots.push(node)
  }
  const sort = (list: OuNode[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name))
    list.forEach((n) => sort(n.children))
  }
  sort(roots)
  return roots
}

/** DN-ul `dn` sta sub `ancestor` (nu e el insusi). */
function isBelow(dn: string, ancestor: string): boolean {
  return dn.toLowerCase().endsWith(',' + ancestor.toLowerCase())
}

function OuPicker({
  ous,
  chosen,
  setChosen,
  missing,
}: {
  ous: DomainOu[]
  chosen: Set<string>
  setChosen: (next: Set<string>) => void
  missing: DomainPanel['coverage']
}) {
  const tree = useMemo(() => buildOuTree(ous), [ous])
  const [filter, setFilter] = useState('')
  const [closed, setClosed] = useState<Set<string>>(() => new Set())
  const chosenList = [...chosen]

  /* Numaratoarea pe OU se arata numai cand lista lipsa e intreaga - altfel ar minti. */
  const complete = missing !== null && missing.missing.length === missing.missingTotal
  const missingIn = (dn: string) => (complete ? missing!.missing.filter((pc) => isBelow(pc.dn, dn)).length : 0)
  const outside = complete ? missing!.missing.filter((pc) => pc.dn !== '' && !/(^|,)OU=/i.test(pc.dn)).length : 0

  const inheritedFrom = (dn: string) => chosenList.find((c) => isBelow(dn, c))

  const toggle = (dn: string, on: boolean) => {
    const next = new Set(chosen)
    if (on) {
      /* Politica legata de parinte ajunge si la copii: copiii bifati devin de prisos. */
      for (const c of chosen) if (isBelow(c, dn)) next.delete(c)
      next.add(dn)
    } else {
      next.delete(dn)
    }
    setChosen(next)
  }

  const row = (node: OuNode, depth: number, path?: string) => {
    const from = inheritedFrom(node.dn)
    const checked = chosen.has(node.dn) || from !== undefined
    const count = missingIn(node.dn)
    const hasKids = path === undefined && node.children.length > 0
    const isClosed = closed.has(node.dn)
    return (
      <div className={`gp-ou${from ? ' inherited' : ''}`} style={{ paddingLeft: 8 + depth * 18 }} data-dn={node.dn}>
        <button
          type="button"
          className={`gp-caret${hasKids ? '' : ' none'}`}
          aria-label={isClosed ? 'Expand' : 'Collapse'}
          tabIndex={hasKids ? 0 : -1}
          onClick={() => {
            const next = new Set(closed)
            if (isClosed) next.delete(node.dn)
            else next.add(node.dn)
            setClosed(next)
          }}
        >
          <i className={isClosed ? 'fas fa-chevron-right' : 'fas fa-chevron-down'}></i>
        </button>
        <label title={node.dn}>
          <input type="checkbox" checked={checked} disabled={from !== undefined} onChange={(e) => toggle(node.dn, e.target.checked)} />
          <span>
            {node.name}
            {path ? <span className="gp-path"> &nbsp;{path}</span> : null}
          </span>
        </label>
        {from ? <span className="gp-tag inc">included</span> : null}
        {count > 0 ? <span className="gp-tag">{count} without agent</span> : null}
      </div>
    )
  }

  const branch = (list: OuNode[], depth: number) => (
    <ul>
      {list.map((node) => (
        <li key={node.dn}>
          {row(node, depth)}
          {node.children.length > 0 && !closed.has(node.dn) ? branch(node.children, depth + 1) : null}
        </li>
      ))}
    </ul>
  )

  const needle = filter.trim().toLowerCase()
  const matches = needle === '' ? [] : ous.filter((o) => (o.name || o.dn).toLowerCase().includes(needle))

  return (
    <>
      <div className="gp-tree" id="agentGpoOus">
        {ous.length > 8 ? (
          <div className="gp-search">
            <input
              type="search"
              className="form-control"
              placeholder="Find an organizational unit"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        ) : null}
        <div className="gp-list">
          {needle === ''
            ? branch(tree, 0)
            : matches.length === 0
              ? <div className="gp-more">No organizational unit matches.</div>
              : (
                <ul>
                  {matches.map((o) => (
                    <li key={o.dn}>{row({ dn: o.dn, name: o.name || o.dn, children: [] }, 0, parentDn(o.dn))}</li>
                  ))}
                </ul>
              )}
        </div>
        <div className="gp-sel">
          <span id="agentGpoOuCount">
            {chosen.size === 0 ? 'Nothing ticked yet' : chosen.size === 1 ? '1 unit ticked' : `${chosen.size} units ticked`}
          </span>
          {chosen.size > 0 ? (
            <button type="button" className="gp-link" onClick={() => setChosen(new Set())}>
              Clear
            </button>
          ) : null}
        </div>
      </div>
      <p className="gp-hint" id="agentGpoMovedHint">
        A computer moved to another organizational unit picks up the new policy only after it restarts.
      </p>
      {outside > 0 ? (
        <p className="gp-hint">
          {outside === 1 ? '1 computer' : `${outside} computers`} without the agent{' '}
          {outside === 1 ? 'sits' : 'sit'} outside every organizational unit (for example in the default Computers
          container). Only Whole domain reaches {outside === 1 ? 'it' : 'them'}.
        </p>
      ) : null}
    </>
  )
}
